import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LucideIcon, TrendingUp, TrendingDown, Info } from "lucide-react";
import { formatCurrency, formatPercentage } from "@/lib/utils";

type MetricType = 'simple' | 'progress' | 'breakdown';

interface BaseMetric {
  type: MetricType;
  title: string;
  icon: LucideIcon;
}

interface SimpleMetric extends BaseMetric {
  type: 'simple';
  value: number;
  unit?: string;
  trend?: "up" | "down";
  description?: string;
}

interface ProgressMetric extends BaseMetric {
  type: 'progress';
  value: number;
  target: number;
  percentage: number;
}

interface BreakdownMetric extends BaseMetric {
  type: 'breakdown';
  values: {
    firm: number;
    soft: number;
    total: number;
  };
}

type MetricCard = SimpleMetric | ProgressMetric | BreakdownMetric;

interface DashboardMetricsProps {
  stats: MetricCard[];
}

export function DashboardMetrics({ stats }: DashboardMetricsProps) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {stats.map((card, index) => {
        if (card.type === 'progress') {
          return <ProgressMetricCard key={card.title} card={card} index={index} />;
        } else if (card.type === 'breakdown') {
          return <BreakdownMetricCard key={card.title} card={card} index={index} />;
        } else {
          return <SimpleMetricCard key={card.title} card={card} index={index} />;
        }
      })}
    </div>
  );
}

function SimpleMetricCard({ 
  card, 
  index 
}: { 
  card: SimpleMetric;
  index: number;
}) {
  return (
    <Card
      className="border border-border shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {card.title}
          </p>
          <card.icon className="h-4 w-4 text-muted-foreground" />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-baseline gap-1">
            <p className="text-3xl md:text-4xl font-bold text-foreground">
              {card.value.toFixed(1)}
            </p>
            {card.unit && (
              <span className="text-2xl font-semibold text-muted-foreground">{card.unit}</span>
            )}
            {card.trend && (
              <div className={`ml-2 ${
                card.trend === 'up' ? 'text-success' : 'text-destructive'
              }`}>
                {card.trend === 'up' ? (
                  <TrendingUp className="h-5 w-5" />
                ) : (
                  <TrendingDown className="h-5 w-5" />
                )}
              </div>
            )}
          </div>
          {card.description && (
            <p className="text-xs text-muted-foreground">
              {card.description}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressMetricCard({ 
  card, 
  index 
}: { 
  card: ProgressMetric;
  index: number;
}) {
  return (
    <Card
      className="border border-border shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {card.title}
          </p>
          <card.icon className="h-4 w-4 text-muted-foreground" />
        </div>
        
        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <p className="text-2xl md:text-3xl font-bold text-foreground">
              {formatCurrency(card.value)}
            </p>
            <span className="text-sm text-muted-foreground">
              / {formatCurrency(card.target)}
            </span>
          </div>
          
          <div className="space-y-1">
            <Progress value={card.percentage} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {formatPercentage(card.percentage)} of plan
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownMetricCard({ 
  card, 
  index 
}: { 
  card: BreakdownMetric;
  index: number;
}) {
  return (
    <Card
      className="border border-border shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <CardContent className="p-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-between mb-3 cursor-help">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {card.title}
                  </p>
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <card.icon className="h-4 w-4 text-muted-foreground" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="space-y-2">
                <p className="font-semibold">Firm Backlog:</p>
                <p className="text-xs">Signed contracts with high probability of execution</p>
                <p className="font-semibold mt-2">Soft Backlog:</p>
                <p className="text-xs">Prospective projects in negotiation or with letters of intent, not yet contracted</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-muted-foreground">Firm</span>
            <span className="text-base font-semibold text-foreground">
              {formatCurrency(card.values.firm, false)}
            </span>
          </div>
          
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-muted-foreground">Soft</span>
            <span className="text-base font-semibold text-foreground">
              {formatCurrency(card.values.soft, false)}
            </span>
          </div>
          
          <div className="border-t border-border pt-2 mt-2">
            <div className="flex justify-between items-baseline">
              <span className="text-xs font-medium text-foreground">Total</span>
              <span className="text-lg font-bold text-foreground">
                {formatCurrency(card.values.total, false)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
